import React from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import { openAddGoal, openModal } from "../../../../../utils/modal-helpers";
import { GOAL_STATE_COLORS } from "../../../nutrition/components/views/viewTokens";
import { GoalsAddButton, GoalsEmptyState } from "./";

export const PhaseSummary = React.memo(
  ({
    trainingPhase,
    nutritionPhase,
    bodyweightGoal,
    nextMilestone,
    dailyTargets = [],
    customGoals = [],
  }) => {
    const { t } = useTranslation("athlete-portal");

    return (
      <div className="phase-summary">
        <div className="phase-summary-cards">
          <div className="summary-card">
            <span className="summary-label">
              {t("annualPlanner.trainingPhase")}
            </span>
            <div className="summary-value">
              {trainingPhase ? (
                <>
                  <span
                    className="summary-dot"
                    style={{ backgroundColor: trainingPhase.color }}
                  />
                  {trainingPhase.name}
                </>
              ) : (
                "—"
              )}
            </div>
          </div>
          <div className="summary-card">
            <span className="summary-label">
              {t("annualPlanner.nutritionPhase")}
            </span>
            <div className="summary-value">
              {nutritionPhase ? (
                <>
                  <span
                    className="summary-dot"
                    style={{ backgroundColor: nutritionPhase.color }}
                  />
                  {nutritionPhase.name}
                </>
              ) : (
                "—"
              )}
            </div>
          </div>
          <div className="summary-card">
            <span className="summary-label">
              {t("annualPlanner.bodyweightGoal")}
            </span>
            <div className="summary-value">
              {bodyweightGoal ? (
                <>
                  {bodyweightGoal.value}
                  {bodyweightGoal.status && (
                    <span
                      className="summary-status"
                      style={{
                        color:
                          GOAL_STATE_COLORS[bodyweightGoal.statusToken]?.text,
                        background:
                          GOAL_STATE_COLORS[bodyweightGoal.statusToken]?.bg,
                      }}
                    >
                      {bodyweightGoal.status}
                    </span>
                  )}
                </>
              ) : (
                "—"
              )}
            </div>
          </div>
          <div className="summary-card">
            <span className="summary-label">
              {t("annualPlanner.nextMilestone")}
            </span>
            <div className="summary-value">
              {nextMilestone ? (
                <>
                  <span className="summary-milestone-label">
                    {nextMilestone.label}
                  </span>
                  <span className="summary-milestone-secondary">
                    {nextMilestone.detail && `${nextMilestone.detail} · `}
                    {nextMilestone.date}
                  </span>
                </>
              ) : (
                "—"
              )}
            </div>
          </div>
        </div>

        <div className="phase-summary-goals">
          <span className="chip info phase-summary-badge">
            {t("annualPlanner.infoOnlyNotDated")}
          </span>

          <div className="phase-summary-goals-content">
            <div className="phase-summary-daily-targets">
              {dailyTargets.length ? (
                <div className="phase-summary-daily-targets-filled">
                  <span className="summary-label">
                    {t("annualPlanner.dailyTargets")}
                  </span>
                  <div className="summary-pills">
                    {dailyTargets.map((target) => (
                      <span className="summary-pill" key={target.label}>
                        <SvgIcon src={target.icon} useCurrentColor />
                        {target.label}
                      </span>
                    ))}
                  </div>
                  {/* steps/sleep/waterIntake - dailyTargets only ever holds
                      an entry per type that's actually set (see
                      usePhaseSummaryData.js), so length 3 means nothing's
                      left to add. */}
                  {dailyTargets.length < 3 && (
                    <GoalsAddButton
                      ctaLabel={t("annualPlanner.setDailyGoals")}
                      onCta={() => openModal("select-daily-goal-type")}
                    />
                  )}
                </div>
              ) : (
                <GoalsEmptyState
                  title={t("annualPlanner.noDailyGoalsTitle")}
                  chipLabel={t("annualPlanner.noDailyGoalsChip")}
                  description={t("annualPlanner.noDailyGoalsDescription")}
                  ctaLabel={t("annualPlanner.setDailyGoals")}
                  // AddGoal.js needs one concrete goal type up front (see its
                  // VALID_GOAL_TYPES gate) - there's no "daily" umbrella type
                  // there, so this opens the small steps/sleep/water picker
                  // modal first instead of guessing which one to jump to.
                  onCta={() => openModal("select-daily-goal-type")}
                />
              )}
            </div>

            <span className="summary-divider" />

            <div className="phase-summary-custom-goals">
              {customGoals.length ? (
                <div className="phase-summary-custom-goals-filled">
                  <span className="summary-label">
                    {t("annualPlanner.customGoals")}
                  </span>
                  <div className="summary-pills">
                    {customGoals.map((goal) => (
                      <span
                        className="summary-pill"
                        key={goal.label}
                        title={goal.label}
                      >
                        {goal.label}
                      </span>
                    ))}
                  </div>
                  <GoalsAddButton
                    ctaLabel={t("annualPlanner.setCustomGoals")}
                    onCta={() => openAddGoal("custom")}
                  />
                </div>
              ) : (
                <GoalsEmptyState
                  title={t("annualPlanner.noCustomGoalsTitle")}
                  chipLabel={t("annualPlanner.noCustomGoalsChip")}
                  description={t("annualPlanner.noCustomGoalsDescription")}
                  ctaLabel={t("annualPlanner.setCustomGoals")}
                  onCta={() => openAddGoal("custom")}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);
